import React, { useCallback, useEffect, useState, useRef } from "react";
import { Button, Input, message, Select, Space, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { debounce, processAccountList, naturalSortSessions } from "../../utils";
import styles from "../../pages/main/Admin/index.module.scss";
import { CheckboxField } from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import * as queries from "../../graphql/queries";
import * as mutations from "../../graphql/mutations";
import ApiKeysModal from "./ApiKeysModal";
import AccountManagementModal, { ACCOUNT_MANAGEMENT_MODAL_COMMAND } from "./AccountManagementModal";
import Service from "../../services";
import { filterTradeStatusList } from "../accountOverview/GroupTable";

const TEMPLATE_USER_NAMES = ["non.admin", "super.admin"];

const AccountsTab = ({ userInfo }) => {
  const { Option } = Select;
  const [panelAccountList, setPanelAccountList] = useState([]);
  const [panelAccountIndex, setPanelAccountIndex] = useState();
  const [accountPermissions, setAccountPermissions] = useState([]);
  const [initialAccountPermissions, setInitialAccountPermissions] = useState([]);
  const [searchText, setSearchText] = useState(""); //搜索文本
  const [apiKeyAccount, setApiKeyAccount] = useState("");
  const [accountManagementProps, setAccountManagementProps] = useState(null);
  const [accountMap, setAccountMap] = useState(new Map());
  const accountPermissionsRef = useRef(accountPermissions);

  const handleSearchChange = (e) => {
    const value = e.target.value; // Extract value from event
    debounce(() => setSearchText(value), 500)();
  };

  const getAdminPanel = async () => {
    const [listAccountPermissions, listExchangeAccounts, listManagedTradeAccountStatus] = await Promise.all([
      API.graphql(graphqlOperation(queries.listCognitoAdminPermissions, {})),
      API.graphql(graphqlOperation(queries.listMonitoringAccounts, {})),
      Service.listManagedTradeAccountStatus(),
    ]);

    const adminAccounts = listAccountPermissions.data.listCognitoAdminPermissions.items.filter(
      (account) => !TEMPLATE_USER_NAMES.includes(account.username)
    );
    const exchangeAccounts = listExchangeAccounts.data.listMonitoringAccounts.items
      .filter((item) => item.disableMonitor === false)
      .map(({ exchange, account, parentAccount }) => ({
        exchange,
        account,
        parentAccount,
      }));

    setInitialAccountPermissions(
      exchangeAccounts.map((exchangeAccount) => ({
        key: `${exchangeAccount.exchange}_${exchangeAccount.account}`,
        tradingAccount: `${exchangeAccount.exchange}_${exchangeAccount.account}`,
        readOnly: false,
        control: false,
      }))
    );
    const initialAccountMap = processAccountList(exchangeAccounts);
    const finalAccountMap = new Map(initialAccountMap);
    if (listManagedTradeAccountStatus && listManagedTradeAccountStatus.flatMap) {
      const filteredAccounts = listManagedTradeAccountStatus
        .flatMap(({ parsedTradeAccountStatusList }) => filterTradeStatusList(parsedTradeAccountStatusList))
        .map((account) => ({
          exchange: account.tradeAccountStatus.exchange,
          account: account.account,
          parentAccount: account.parentAccount,
        }));

      // Process the filtered accounts into a new map
      const updatedAccountMap = processAccountList(filteredAccounts);
      // Merge entries from updatedAccountMap into finalAccountMap
      for (const [key, newValue] of updatedAccountMap.entries()) {
        if (finalAccountMap.has(key)) {
          const existingValue = finalAccountMap.get(key);
          finalAccountMap.set(key, {
            usdSession: [...new Set([...existingValue.usdSession, ...newValue.usdSession])],
            coinSession: [...new Set([...existingValue.coinSession, ...newValue.coinSession])],
            spotSession: [...new Set([...existingValue.spotSession, ...newValue.spotSession])],
            spotAlgoSession: [...new Set([...existingValue.spotAlgoSession, ...newValue.spotAlgoSession])],
            futureAlgoSession: [...new Set([...existingValue.futureAlgoSession, ...newValue.futureAlgoSession])],
            fundingRateArbitrageSession: [
              ...new Set([...existingValue.fundingRateArbitrageSession, ...newValue.fundingRateArbitrageSession]),
            ],
          });
        } else {
          finalAccountMap.set(key, { ...newValue });
        }
      }
    } else {
      console.error("Unexpected structure in listManagedTradeAccountStatus:", listManagedTradeAccountStatus);
    }
    setAccountMap(finalAccountMap);
    setPanelAccountList(adminAccounts);
  };

  const update = async () => {
    const input = {
      email: panelAccountList[panelAccountIndex].email,
      managedAccounts: accountPermissions.map((e) => {
        const permissions = [];
        if (e.readOnly) permissions.push("READ");
        if (e.control) permissions.push("CONTROL");
        return {
          name: e.tradingAccount,
          permissions,
        };
      }),
    };
    const hideLoading = message.loading("Updating...", 0);
    try {
      await API.graphql(graphqlOperation(mutations.updateCognitoAdminPermission, { input }));
      await getAdminPanel();
      hideLoading();
      message.success("Successfully updated");
    } catch (error) {
      hideLoading();
      message.error(String(error));
    }
  };

  const changePanelAccount = (index) => {
    setPanelAccountIndex(index);
    updateAccountPermissions(index, accountPermissions);
  };

  const updateAccountPermissions = useCallback(
    (index, currentPermissions) => {
      const managedAccountPermissions = panelAccountList[index].managedAccounts
        ? panelAccountList[index].managedAccounts.map((e) => {
            return {
              key: e.name,
              tradingAccount: e.name,
              readOnly: e.permissions.includes("READ"),
              control: e.permissions.includes("CONTROL"),
            };
          })
        : [];

      const accountPermissionsMap = new Map();
      currentPermissions.forEach((permission) => {
        accountPermissionsMap.set(permission.tradingAccount, {
          ...permission,
          readOnly: false,
          control: false,
        });
      });

      // Overwrite with managed account permissions
      managedAccountPermissions.forEach((permission) => {
        accountPermissionsMap.set(permission.tradingAccount, permission);
        const usdSession = accountMap.get(permission.tradingAccount)?.usdSession || [];
        usdSession.forEach((child) => {
          if (accountPermissionsMap.has(child)) {
            accountPermissionsMap.set(child, {
              key: child,
              tradingAccount: child,
              readOnly: permission.readOnly,
              control: permission.control,
            });
          }
        });
      });

      setAccountPermissions([...accountPermissionsMap.values()]);
    },
    [accountMap, panelAccountList]
  );

  const handleCheckboxChange = (tradingAccount, permissionType) => {
    const newPermissions = accountPermissions.map((account) => {
      if (
        account.tradingAccount === tradingAccount ||
        (accountMap.get(tradingAccount)?.usdSession || []).includes(account.tradingAccount)
      ) {
        return {
          ...account,
          [permissionType]: !account[permissionType],
        };
      }
      return account;
    });

    setAccountPermissions(newPermissions);
  };

  useEffect(() => {
    getAdminPanel();
  }, []);

  useEffect(() => {
    if (panelAccountIndex >= 0) {
      updateAccountPermissions(panelAccountIndex, accountPermissionsRef.current);
    }
  }, [updateAccountPermissions, panelAccountIndex, accountPermissionsRef]);

  // update the active account information at load time. Subsequent changes are handled by changePanelAccount
  useEffect(() => {
    if (!(panelAccountIndex >= 0)) {
      const defaultIndex = panelAccountList.findIndex((e) => e.email === userInfo.email);
      if (defaultIndex >= 0) {
        setPanelAccountIndex(defaultIndex);
        updateAccountPermissions(defaultIndex, initialAccountPermissions);
      }
    }
  }, [initialAccountPermissions, panelAccountIndex, panelAccountList, updateAccountPermissions, userInfo]);

  const columns = [
    {
      title: "Trading Account",
      dataIndex: "tradingAccount",
      key: "tradingAccount",
      width: "25%",
    },
    {
      title: "Read Only",
      dataIndex: "readOnly",
      key: "readOnly",
      width: "10%",
      render: (_, record) => (
        <CheckboxField
          checked={record.readOnly}
          isDisabled={record.control}
          onChange={() => handleCheckboxChange(record.tradingAccount, "readOnly")}
          label=""
          name={`readOnly_${record.tradingAccount}`}
        />
      ),
    },
    {
      title: "Control (Trade/Stop/Empty)",
      dataIndex: "control",
      key: "control",
      width: "15%",
      render: (_, record) => (
        <CheckboxField
          checked={record.control}
          isDisabled={!record.readOnly}
          onChange={() => handleCheckboxChange(record.tradingAccount, "control")}
          label=""
          name={`control_${record.tradingAccount}`}
        />
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: "45%",
      render: (_, record) => (
        <div>
          <Space>
            <Button
              onClick={() => {
                setApiKeyAccount(record.tradingAccount);
              }}
            >
              Update API Keys
            </Button>
            <Button
              onClick={() => {
                setAccountManagementProps({
                  accountManagementCommand: ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_SESSION,
                  exchangeAccount: record.tradingAccount,
                });
              }}
              disabled={record.tradingAccount.startsWith("cryptocom")}
            >
              Create New Session
            </Button>
            <Button
              onClick={() => {
                setAccountManagementProps({
                  accountManagementCommand: ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT,
                  exchangeAccount: record.tradingAccount,
                  accountMap: accountMap,
                });
              }}
            >
              {ACCOUNT_MANAGEMENT_MODAL_COMMAND.DELETE_ACCOUNT}
            </Button>
          </Space>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.admin}>
      <div className={styles.adminHeader}>
        <div className={styles.title}>Admin</div>
        <div className={styles.adminPanel}>
          Admin Panel Accounts:
          <Select
            showSearch
            style={{ width: 320, margin: "0 10px" }}
            value={panelAccountIndex}
            onChange={(i) => changePanelAccount(i)}
            placeholder="Search for an account"
            optionFilterProp="children"
          >
            {panelAccountList.map((e, i) => (
              <Option key={i} value={i}>
                {e.username}
              </Option>
            ))}
          </Select>
        </div>
      </div>
      <div className={styles.adminSearch}>
        <Input placeholder="Search Trading Accounts" onChange={handleSearchChange} suffix={<SearchOutlined style={{ color: "#999" }} />} />
      </div>
      <div className={styles.adminTable}>
        <Table
          dataSource={accountPermissions.filter((e) => e.tradingAccount.indexOf(searchText) > -1 && accountMap.has(e.tradingAccount))}
          columns={columns}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                dataSource={[
                  {
                    key: "usd",
                    type: "Sub USD-M Sessions",
                    sessions: accountMap.get(record.tradingAccount).usdSession?.sort(naturalSortSessions).join(", ") || "None",
                  },
                  {
                    key: "coin",
                    type: "Coin Futures Sessions",
                    sessions: accountMap.get(record.tradingAccount).coinSession?.sort(naturalSortSessions).join(", ") || "None",
                  },
                  {
                    key: "spot",
                    type: "Spot Sessions",
                    sessions: accountMap.get(record.tradingAccount).spotSession?.sort(naturalSortSessions).join(", ") || "None",
                  },
                  {
                    key: "spotAlgo",
                    type: "Spot Algo Sessions",
                    sessions: accountMap.get(record.tradingAccount).spotAlgoSession?.sort(naturalSortSessions).join(", ") || "None",
                  },
                  {
                    key: "futureAlgo",
                    type: "Future Algo Sessions",
                    sessions: accountMap.get(record.tradingAccount).futureAlgoSession?.sort(naturalSortSessions).join(", ") || "None",
                  },
                  {
                    key: "fundingRateArbitrage",
                    type: "Funding Rate Arbitrage Sessions",
                    sessions:
                      accountMap.get(record.tradingAccount).fundingRateArbitrageSession?.sort(naturalSortSessions).join(", ") || "None",
                  },
                ]}
                columns={[{ dataIndex: "type", width: "200px" }, { dataIndex: "sessions" }]}
                pagination={false}
                showHeader={false}
                size="small"
              />
            ),
            rowExpandable: (record) =>
              accountMap.get(record.tradingAccount).usdSession?.length > 0 ||
              accountMap.get(record.tradingAccount).coinSession?.length > 0 ||
              accountMap.get(record.tradingAccount).spotSession?.length > 0 ||
              accountMap.get(record.tradingAccount).spotAlgoSession?.length > 0 ||
              accountMap.get(record.tradingAccount).futureAlgoSession?.length > 0 ||
              accountMap.get(record.tradingAccount).fundingRateArbitrageSession?.length > 0,
          }}
          pagination={false}
          scroll={{ y: 600 }}
        />
      </div>
      <div className={styles.adminUpdate}>
        <Button type="primary" disabled={!panelAccountList[panelAccountIndex]} onClick={() => update()}>
          Update
        </Button>
      </div>
      <div
        className={styles.addNewAccount + " pointer"}
        onClick={() =>
          setAccountManagementProps({
            accountManagementCommand: ACCOUNT_MANAGEMENT_MODAL_COMMAND.NEW_ACCOUNT,
          })
        }
      >
        + Add new account
      </div>
      {/* ------------------------ Modal Section Starts --------------------------------- */}
      {apiKeyAccount && <ApiKeysModal apiKeyAccount={apiKeyAccount} closeModal={() => setApiKeyAccount("")} />}
      {accountManagementProps && (
        <AccountManagementModal
          {...accountManagementProps}
          closeModal={() => setAccountManagementProps(null)}
          onConfirmCallback={getAdminPanel}
        />
      )}
      {/* ------------------------ Modal Section Ends --------------------------------- */}
    </div>
  );
};

export default AccountsTab;
